import CloseIcon from '@mui/icons-material/Close';
import ExploreIcon from '@mui/icons-material/Explore';
import FilterListIcon from '@mui/icons-material/FilterList';
import MapIcon from '@mui/icons-material/Map';
import PlaceIcon from '@mui/icons-material/Place';
import SearchIcon from '@mui/icons-material/Search';
import {
    Box,
    Chip,
    Divider,
    IconButton,
    InputAdornment,
    Paper,
    Stack,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
    alpha,
    useTheme,
} from '@mui/material';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import CloakFetcher from 'cloak-auth/src/Client/CloakFetcher';
import { useGetInstances, useGetPublicInstances, useGetUserDeviceGroupsLastSeen } from 'gen/client';
import { useCallback, useEffect, useMemo, useState } from 'react';
import useSWR from 'swr';

import ReactMap from '@ui/Components/Map/ReactMap';
import { InstanceDescription } from '@ui/Types/AuthServer';

import InfoButton from 'kerberus-components/src/Components/Icons/InfoButton';
import { useHasNext } from 'kerberus-components/src/Hooks/useHasNext';

import ViewContainer from '../../Components/Layout/ViewContainer';
import NewPlacesList from './Discover/NewPlacesList';
import InstanceList from './My/InstanceList';

function PlacesView() {
    const [mode, setMode] = useState<'my' | 'discover'>('my');
    const theme = useTheme();

    const { data: onlineDevs } = useGetUserDeviceGroupsLastSeen(
        {},
        {
            swr: {
                refreshInterval: 60_000,
            },
        },
    );

    const { data: myInstances = [], isLoading: loadingMy } = useGetInstances();
    const { data: publicInstances = [], isLoading: loadingPublic } = useGetPublicInstances();

    const discoverInstances = publicInstances.filter((pub) => !myInstances.some((mine) => mine.id === pub.id));

    // Build activity map from onlineDevs: pick latest timestamp per instance_role
    const activityByRole = useMemo(() => {
        const map: Record<string, { group_id: string; timestamp: string }> = {};
        onlineDevs?.forEach((d) => {
            const prev = map[d.instance_role];
            if (!prev || new Date(d.timestamp).getTime() > new Date(prev.timestamp).getTime()) {
                map[d.instance_role] = { group_id: d.group_id, timestamp: d.timestamp };
            }
        });
        return map;
    }, [onlineDevs]);

    const [search, setSearch] = useState('');
    const hasNext = useHasNext();

    const handleModeChange = (_: unknown, value: 'my' | 'discover' | null) => {
        if (value) {
            setMode(value);
        }
    };

    // Build the list of instance (id, domain) pairs we want descriptions for
    const allRelevantInstances = useMemo(
        () => [...myInstances, ...discoverInstances],
        [myInstances, discoverInstances],
    );

    // domain -> id (as string) mapping and unique sorted domains list
    const domainToId = useMemo(() => {
        const m = new Map<string, string>();
        allRelevantInstances.forEach((i) => {
            const dom = i.domain as unknown as string | undefined | null;
            if (typeof dom === 'string' && dom.length > 0) {
                m.set(dom, String(i.id));
            }
        });
        return m;
    }, [allRelevantInstances]);

    const domains: string[] = useMemo(() => Array.from(domainToId.keys()).sort(), [domainToId]);

    // SWR cache for instance descriptions (batched by domains)
    const { data: swrDescriptions } = useSWR<Record<string, InstanceDescription>>(
        domains.length ? (['instance-descs', ...domains] as const) : null,
        async () => {
            const results = await Promise.all(
                domains.map((domain) => CloakFetcher('tasks/instance_description', domain)),
            );
            const map: Record<string, InstanceDescription> = {};
            for (let i = 0; i < domains.length; i += 1) {
                const domain = domains[i];
                if (!domain) {
                    continue;
                }
                const data = results[i] as InstanceDescription | undefined;
                const id = domainToId.get(domain);
                if (id && data) {
                    map[id] = data;
                }
            }
            return map;
        },
        {
            dedupingInterval: 1000 * 60 * 5, // 5 min
            revalidateIfStale: false,
            revalidateOnFocus: false,
            revalidateOnReconnect: false,
        },
    );

    const descriptions = useMemo<Record<string, InstanceDescription>>(() => swrDescriptions ?? {}, [swrDescriptions]);

    const normalizedNeedle = search.trim().toLowerCase();

    const buildHaystack = useCallback(
        (id: string | number, fallbackName: string): string => {
            const desc = descriptions[String(id)];
            const parts: string[] = [fallbackName];
            if (desc) {
                if (desc.name) {
                    parts.push(desc.name);
                }
                if (desc.description) {
                    parts.push(desc.description);
                }
                if (desc.address) {
                    parts.push(desc.address);
                }
                if (desc.instance_type) {
                    parts.push(desc.instance_type);
                } // eslint-disable-line @typescript-eslint/no-unnecessary-condition
            }
            return parts.join(' ').toLowerCase();
        },
        [descriptions],
    );

    const filteredMy = useMemo(() => {
        if (mode !== 'my') {
            return [];
        }
        if (!normalizedNeedle) {
            return myInstances;
        }
        return myInstances.filter((i) => buildHaystack(i.id, i.name).includes(normalizedNeedle));
    }, [mode, myInstances, normalizedNeedle, buildHaystack]);

    const filteredDiscover = useMemo(() => {
        if (mode !== 'discover') {
            return [];
        }
        if (!normalizedNeedle) {
            return discoverInstances;
        }
        return discoverInstances.filter((i) => buildHaystack(i.id, i.name).includes(normalizedNeedle));
    }, [mode, discoverInstances, normalizedNeedle, buildHaystack]);

    const title = 'Places';
    const subtitle = mode === 'my' ? 'Your joined locations' : 'Discover & join new locations';

    const countMy = myInstances.length;
    const countDiscover = discoverInstances.length;

    const loading = mode === 'my' ? loadingMy : loadingPublic;

    const [mapOpen, setMapOpen] = useState(false);
    const openMap = () => setMapOpen(true);
    const closeMap = () => setMapOpen(false);
    const [isMobile, setIsMobile] = useState<boolean>(() => {
        try {
            return window.innerWidth < (theme.breakpoints.values.md || 900);
        } catch {
            return false;
        }
    });

    useEffect(() => {
        const handle = () => {
            try {
                setIsMobile(window.innerWidth < (theme.breakpoints.values.md || 900));
            } catch {
                setIsMobile(false);
            }
        };
        window.addEventListener('resize', handle);
        return () => window.removeEventListener('resize', handle);
    }, [theme.breakpoints.values.md]);

    return (
        <ViewContainer sx={{ py: { sm: 2, xs: 1.5 }, width: '100%' }}>
            <Stack spacing={2} sx={{ width: '100%' }}>
                {/* Header */}
                <Stack spacing={0.5} sx={{ px: { md: 0, xs: 0.5 } }}>
                    <Typography sx={{ color: 'primary.main', fontWeight: 600, letterSpacing: 0.2 }} variant="h5">
                        {title}
                    </Typography>
                    <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
                        <Typography sx={{ color: 'text.secondary', fontWeight: 500 }} variant="body2">
                            {subtitle}
                        </Typography>
                        <InfoButton title="Browse your places or discover new ones. Use search to quickly filter." />
                    </Stack>
                </Stack>

                {/* Mode + Search Bar */}
                <Paper
                    elevation={0}
                    sx={{
                        backdropFilter: 'blur(4px)',
                        background: alpha(theme.palette.primary.main, 0.03),
                        border: `1px solid ${alpha(theme.palette.primary.main, 0.08)}`,
                        borderRadius: 3,
                        p: { sm: 1.25, xs: 1 },
                    }}
                >
                    <Stack
                        direction={{ md: 'row', xs: 'column' }}
                        spacing={1.5}
                        sx={{ alignItems: { md: 'center' }, justifyContent: 'space-between' }}
                    >
                        <ToggleButtonGroup
                            exclusive
                            onChange={handleModeChange}
                            size="small"
                            sx={{ backgroundColor: alpha(theme.palette.primary.main, 0.06), borderRadius: 2, p: 0.5 }}
                            value={mode}
                        >
                            <ToggleButton sx={{ gap: 0.75, px: 2, textTransform: 'none' }} value="my">
                                <PlaceIcon fontSize="small" />
                                <Typography sx={{ fontWeight: 600 }} variant="body2">
                                    My{' '}
                                    <Chip
                                        component="span"
                                        label={countMy}
                                        size="small"
                                        sx={{ fontSize: 11, height: 18, ml: 0.5 }}
                                    />
                                </Typography>
                            </ToggleButton>
                            <ToggleButton sx={{ gap: 0.75, px: 2, textTransform: 'none' }} value="discover">
                                <ExploreIcon fontSize="small" />
                                <Typography sx={{ fontWeight: 600 }} variant="body2">
                                    Discover
                                    <Chip
                                        component="span"
                                        label={countDiscover}
                                        size="small"
                                        sx={{ fontSize: 11, height: 18, ml: 0.5 }}
                                    />
                                </Typography>
                            </ToggleButton>
                        </ToggleButtonGroup>

                        <Stack direction="row" spacing={1} sx={{ maxWidth: 520, ml: { md: 2 }, width: '100%' }}>
                            <TextField
                                fullWidth
                                label={mode === 'my' ? 'Search my places' : 'Search new places'}
                                onChange={(e) => setSearch(e.target.value)}
                                size="small"
                                slotProps={{
                                    input: {
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <SearchIcon />
                                            </InputAdornment>
                                        ),
                                    },
                                }}
                                type="search"
                                value={search}
                                variant="outlined"
                            />
                            {!!hasNext && (
                                <IconButton
                                    disabled={myInstances.length === 0 && publicInstances.length === 0}
                                    onClick={openMap}
                                    sx={{
                                        '&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.18) },
                                        backgroundColor: alpha(theme.palette.primary.main, 0.08),
                                        borderRadius: 2,
                                        height: 40,
                                        width: 44,
                                    }}
                                >
                                    <Stack
                                        spacing={0.1}
                                        sx={{
                                            alignItems: 'center',
                                        }}
                                    >
                                        <MapIcon fontSize="small" />
                                        <Typography sx={{ fontSize: 9, fontWeight: 600 }} variant="caption">
                                            Map
                                        </Typography>
                                    </Stack>
                                </IconButton>
                            )}
                            <IconButton
                                sx={{
                                    '&:hover': { backgroundColor: alpha(theme.palette.text.primary, 0.12) },
                                    backgroundColor: alpha(theme.palette.text.primary, 0.04),
                                    borderRadius: 2,
                                    display: 'none', // Hide filter icon for now
                                    height: 40,
                                    width: 44,
                                }}
                            >
                                <FilterListIcon fontSize="small" />
                            </IconButton>
                        </Stack>
                    </Stack>
                </Paper>

                <Divider />

                <Box sx={{ flex: 1, minHeight: '50dvh' }}>
                    {loading ? null : mode === 'my' ? (
                        <InstanceList
                            activityByRole={activityByRole}
                            descriptions={descriptions}
                            instances={filteredMy}
                        />
                    ) : (
                        <NewPlacesList instances={filteredDiscover} />
                    )}
                </Box>
            </Stack>
            {/*  Map modal */}
            <Dialog
                fullScreen={isMobile}
                fullWidth={!isMobile}
                maxWidth={isMobile ? false : 'lg'}
                onClose={closeMap}
                open={mapOpen}
                slotProps={{
                    paper: {
                        sx: {
                            backgroundColor: 'background.default',
                            ...(isMobile
                                ? {}
                                : {
                                      borderRadius: 3,
                                  }),

                            paddingBottom: 'var(--safe-area-inset-bottom, 0px)',
                            paddingLeft: 'var(--safe-area-inset-left, 0px)',
                            paddingRight: 'var(--safe-area-inset-right, 0px)',
                            paddingTop: 'var(--safe-area-inset-top, 0px)',
                        },
                    },
                }}
            >
                <DialogContent
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        height: isMobile ? '100dvh' : { md: '70dvh', xs: '80dvh' },
                        p: 0,
                        position: 'relative',
                        width: '100%',
                    }}
                >
                    {/* Close button */}
                    <IconButton
                        onClick={closeMap}
                        size="small"
                        sx={{
                            '&:hover': { backgroundColor: (t) => t.palette.background.paper },
                            backgroundColor: (t) => t.palette.background.paper,
                            boxShadow: 2,
                            position: 'absolute',
                            right: 12,
                            top: 10,
                            zIndex: 10,
                        }}
                    >
                        <CloseIcon fontSize="small" />
                    </IconButton>
                    {/* Map wrapper (centers on desktop, full on mobile) */}
                    <Box
                        sx={{
                            flex: 1,
                            width: '100%',
                            ...(isMobile
                                ? {}
                                : {
                                      mx: 'auto',
                                      width: '100%',
                                  }),
                        }}
                    >
                        {/* <InstanceMap handleClose={closeMap} isMobile={isMobile} /> */}
                        <ReactMap handleClose={closeMap} />
                    </Box>
                </DialogContent>
            </Dialog>
        </ViewContainer>
    );
}

export default PlacesView;
